import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ChartLineUp, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBnbGFzcyUyMGJ1aWxkaW5nJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODIzMzE3NjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  const fill = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Visual panel */}
      <div className="relative hidden w-1/2 lg:block">
        <img src={LOGIN_BG} alt="Edificio corporativo" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-primary">
              <ChartLineUp weight="duotone" size={22} aria-hidden="true" />
            </div>
            <span className="font-heading text-lg font-bold">ZAE KPI</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">
              Inteligencia operativa<br />para decisiones ejecutivas.
            </h1>
            <p className="mt-4 max-w-md text-base text-white/80">
              Administra indicadores, variables y fórmulas en una plataforma multiempresa
              preparada para integrarse con Odoo 18 SH.
            </p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold tracking-tight">Bienvenido</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Ingresa tus credenciales para acceder al panel.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label="Mostrar contraseña"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? "Ingresando…" : "Iniciar sesión"}
              {!loading && <ArrowRight size={18} className="ml-1" aria-hidden="true" />}
            </Button>
          </form>

          <div className="mt-8 rounded-lg border border-border bg-card p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Cuentas de demostración
            </p>
            <div className="space-y-1.5 text-sm">
              {[
                ["Administrador", "admin@kpicorp.com", "Admin123!"],
                ["Capturista", "capturista@kpicorp.com", "Captura123!"],
                ["Ejecutivo", "ejecutivo@kpicorp.com", "Ejecutivo123!"],
              ].map(([role, em, pw]) => (
                <button
                  key={em}
                  type="button"
                  onClick={() => fill(em, pw)}
                  className="flex w-full items-center justify-between rounded-md px-2 py-1 text-left hover:bg-accent"
                  data-testid={`demo-fill-${role.toLowerCase()}`}
                >
                  <span className="font-medium">{role}</span>
                  <span className="font-mono text-xs text-muted-foreground">{em}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
